import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Cake, Gift, Church, Bell, Heart, CalendarDays, PartyPopper } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUpcomingEvents } from '@/hooks/use-upcoming-events'
import type { UpcomingEvent, UpcomingEventKind } from '@/lib/types'

const kindIcon: Record<UpcomingEventKind, typeof Cake> = {
  birthday: Cake,
  anniversary: Heart,
  nameday: Church,
  reminder: Bell,
}

const kindColor: Record<UpcomingEventKind, string> = {
  birthday: 'text-coral',
  anniversary: 'text-coral',
  nameday: 'text-lilac-foreground dark:text-lilac',
  reminder: 'text-primary',
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

function EventRow({ event }: { event: UpcomingEvent }) {
  const { t } = useTranslation()
  const Icon = kindIcon[event.kind]
  const label =
    event.kind === 'birthday'
      ? t('upcoming.birthdayOf', "{{name}}'s birthday", { name: event.title })
      : event.kind === 'anniversary'
        ? t('upcoming.anniversaryOf', "{{name}}'s anniversary", { name: event.title })
        : event.kind === 'nameday'
          ? t('upcoming.nameDayOf', "{{name}}'s name day", { name: event.title })
          : event.title

  const inner = (
    <div className="hover:bg-muted/50 flex items-center gap-2.5 rounded-lg p-2 transition-colors">
      {event.avatarUrl ? (
        <img src={event.avatarUrl} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" />
      ) : event.contactName ? (
        <div className="bg-primary/10 text-primary flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold">
          {initialsOf(event.contactName)}
        </div>
      ) : (
        <div className="bg-muted flex h-7 w-7 shrink-0 items-center justify-center rounded-full">
          <Icon className={cn('h-3.5 w-3.5', kindColor[event.kind])} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{label}</p>
      </div>
      <Icon className={cn('h-4 w-4 shrink-0', kindColor[event.kind])} />
    </div>
  )

  return event.contactId ? (
    <Link to={`/contacts/${event.contactId}`}>{inner}</Link>
  ) : (
    <Link to="/reminders">{inner}</Link>
  )
}

export function UpcomingEventsWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useUpcomingEvents()

  if (isLoading || !data) return null

  const saintToday = data.saints.today
  const saintTomorrow = data.saints.tomorrow

  const buckets: { key: 'today' | 'tomorrow' | 'week'; label: string }[] = [
    { key: 'today', label: t('upcoming.today', 'Today') },
    { key: 'tomorrow', label: t('upcoming.tomorrow', 'Tomorrow') },
    { key: 'week', label: t('upcoming.thisWeek', 'Next 7 days') },
  ]

  const hasEvents = data.events.length > 0

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <CalendarDays className="text-primary h-4 w-4" />
        <h2 className="font-heading text-base font-bold">
          {t('upcoming.title', 'Upcoming events')}
        </h2>
      </div>

      {/* Saint of the day + tomorrow */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-surface-container-low rounded-xl p-3">
          <div className="text-on-surface-variant mb-1 flex items-center gap-1.5 text-[10px] tracking-widest uppercase">
            <Church className="h-3 w-3" />
            {t('upcoming.saintToday', 'Saint of the day')}
          </div>
          <p className="text-sm font-semibold">
            {saintToday.names.length > 0
              ? saintToday.names.join(', ')
              : (saintToday.holiday ?? '—')}
          </p>
          {saintToday.holiday && saintToday.names.length > 0 && (
            <p className="text-on-surface-variant text-[11px]">{saintToday.holiday}</p>
          )}
        </div>
        <div className="bg-surface-container-low rounded-xl p-3">
          <div className="text-on-surface-variant mb-1 flex items-center gap-1.5 text-[10px] tracking-widest uppercase">
            <Church className="h-3 w-3" />
            {t('upcoming.saintTomorrow', 'Tomorrow')}
          </div>
          <p className="text-sm font-semibold">
            {saintTomorrow.names.length > 0
              ? saintTomorrow.names.join(', ')
              : (saintTomorrow.holiday ?? '—')}
          </p>
          {saintTomorrow.holiday && saintTomorrow.names.length > 0 && (
            <p className="text-on-surface-variant text-[11px]">{saintTomorrow.holiday}</p>
          )}
        </div>
      </div>

      {/* Name days to celebrate today */}
      {data.nameDaysToday.length > 0 && (
        <div className="bg-lilac-soft rounded-xl p-3">
          <div className="text-lilac-foreground dark:text-lilac mb-2 flex items-center gap-1.5 text-xs font-semibold">
            <PartyPopper className="h-3.5 w-3.5" />
            {t('upcoming.wishNameDay', 'Wish a happy name day today')}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {data.nameDaysToday.map((c) => {
              const name = [c.firstName, c.lastName].filter(Boolean).join(' ')
              return (
                <Link
                  key={c.id}
                  to={`/contacts/${c.id}`}
                  className="bg-surface-container-high hover:bg-surface-container inline-flex items-center gap-1.5 rounded-full py-1 pr-3 pl-1 text-xs font-medium transition-colors"
                >
                  {c.avatarUrl ? (
                    <img src={c.avatarUrl} alt="" className="h-5 w-5 rounded-full object-cover" />
                  ) : (
                    <span className="bg-primary/10 text-primary flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold">
                      {initialsOf(name)}
                    </span>
                  )}
                  {name}
                </Link>
              )
            })}
          </div>
        </div>
      )}

      {/* Grouped upcoming events */}
      {hasEvents ? (
        <div className="space-y-3">
          {buckets.map((bucket) => {
            const items = data.events.filter((e) => e.bucket === bucket.key)
            if (items.length === 0) return null
            return (
              <div key={bucket.key} className="bg-card rounded-2xl border p-3">
                <p className="text-on-surface-variant mb-1.5 px-1 text-[11px] font-semibold tracking-wide uppercase">
                  {bucket.label}
                </p>
                <div className="space-y-0.5">
                  {items.map((event) => (
                    <EventRow key={event.id} event={event} />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="bg-surface-container-low flex items-center gap-2 rounded-xl p-4">
          <Gift className="text-on-surface-variant h-4 w-4" />
          <p className="text-on-surface-variant text-sm">
            {t('upcoming.none', 'No events in the next 7 days')}
          </p>
        </div>
      )}
    </section>
  )
}
